import React, {
    FocusEvent,
    forwardRef,
    MouseEvent,
    PropsWithChildren,
    ReactNode,
    Ref,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react"
import {
    Checkbox,
    CheckboxProps,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    IconButton,
    InputAdornment,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Radio,
    Select,
    SelectChangeEvent,
    SelectProps,
    TextField,
    TextFieldProps,
    Autocomplete,
    AutocompleteProps,
    AutocompleteValue,
    Switch,
    Typography,
    SwitchProps,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FormikValues, useField, useFormikContext } from "formik"
import { StrictOmit, StringKeyOf } from "../../../lib/lang"
import { StandardLoadingButton, StandardLoadingButtonProps } from "../buttons/LoadingButton"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { RichEditor } from "../editor/RichEditor"
import {
    DatePicker,
    DatePickerProps,
    DateTimePicker,
    DateTimePickerProps,
    renderTimeViewClock,
    TimePicker,
    TimePickerProps,
} from "@mui/x-date-pickers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck, faCircleXmark, faPen } from "@fortawesome/pro-light-svg-icons"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect"
import { DateTime } from "luxon"

export type FormikTextFieldProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
    }
>

const useStyles = makeStyles()((_theme) => ({
    textInput: {
        // minHeight: "4rem",
    },
    switch: {
        width: "100%",
    },
    switchLabel: {
        "&.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "space-between",
        },
    },
    customChip: {
        display: "inline-flex",
        fontSize: 12,
        lineHeight: 1,
        borderRadius: 4,
        border: "1px solid #cbd5e1",
        background: `#F8FAFC`,
        padding: `4px 15px`,
        color: "#64748B",
        transition: "0.2s linear all",
        "& .MuiFormControlLabel-label": {
            textTransform: "capitalize",
        },
        "&.MuiFormControlLabel-root": {
            margin: 0,
        },
        "&:hover": {
            border: "1px solid #86EFAC",
            color: "#15803D",
        },
    },
    customChipChecked: {
        border: "1px solid #86EFAC",
        background: `#86EFAC55`,
        color: "#15803D",
    },
    hiddenField: {
        display: "none",
    },
    formSidebarHeading: {
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "-0.5px",
        color: "#64748B",
    },
    formSidebarHeadingPadding: {
        paddingBottom: "2rem",
    },
    timePickerIconButton: {
        "& .MuiSvgIcon-root": {
            fontSize: "1.3rem",
        },
    },
    noteTextInput: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: tailwindPalette.amber[50],
            "& fieldset": {
                borderColor: tailwindPalette.amber[300],
            },
            "&.Mui-disabled": {
                backgroundColor: tailwindPalette.amber[50],
                "& fieldset": {
                    borderColor: tailwindPalette.amber[300],
                },
            },
            "&:hover fieldset": {
                borderColor: tailwindPalette.amber[300],
            },
        },
    },
    icon: {
        color: tailwindPalette.amber[700],
        fontSize: "1rem",
    },
    multilineAdornment: {
        position: "absolute",
        top: ".8rem",
        right: 0,
    },
}))

export function FormikTextField<Values extends FormikValues>({
    name,
    helperText,
    ...rest
}: FormikTextFieldProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)

    const { submitCount } = useFormikContext<Values>()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    return (
        <TextField
            name={name}
            value={field.value}
            error={showError}
            helperText={showError ? meta.error : helperText}
            onBlur={field.onBlur}
            onChange={field.onChange}
            className={classes.textInput}
            {...rest}
        />
    )
}

type FormikRichTextEditorProps<Values extends FormikValues> = Readonly<{
    name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
    placeholder?: string
    toolbar?: string
    helperText?: string
    onCharacterCountChange: (count: number) => void
}>

export function FormikRichTextEditor<Values extends FormikValues>({
    name,
    placeholder = "",
    helperText,
    toolbar,
    onCharacterCountChange,
    ...rest
}: FormikRichTextEditorProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)
    const handleChange = useCallback(
        (value: string) => {
            const handler = field.onChange(name)
            handler(value)
        },
        [field, name],
    )

    return (
        <FormControl error={Boolean(meta.error)} fullWidth>
            <RichEditor
                placeholder={placeholder}
                value={field.value}
                onBlur={field.onBlur}
                onChange={handleChange}
                toolbar={toolbar}
                onCharacterCountChange={onCharacterCountChange}
                {...rest}
            />
            <FormHelperText>{meta.error || helperText}</FormHelperText>
        </FormControl>
    )
}

type FormikAutoCompleteProps<Values extends FormikValues, TOptions> = Readonly<
    StrictOmit<AutocompleteProps<TOptions, false, false, false>, "onChange" | "value"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik,
        helperText?: string
        label: string
        InputProps?: TextFieldProps["InputProps"]
    }
>

export function FormikAutoComplete<Values extends FormikValues, TOption>({
    name,
    label,
    helperText,
    InputProps,
    ...rest
}: FormikAutoCompleteProps<Values, TOption>) {
    const { classes } = useStyles()
    const [field, meta, helpers] = useField<TOption | null>(name)

    const handleChange = useCallback(
        (_event: SyntheticEvent<Element, Event>, value: AutocompleteValue<TOption, false, false, false>) => {
            helpers.setValue(value)
        },
        [field, name],
    )

    return (
        <Autocomplete
            value={field.value}
            onChange={handleChange}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    name={name}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={(meta.touched && meta.error) || helperText}
                    InputProps={{
                        ...params.InputProps,
                        ...InputProps,
                    }}
                />
            )}
        />
    )
}

type FormikMultipleAutoCompleteProps<Values extends FormikValues, TOption, TValue> = Readonly<
    StrictOmit<
        AutocompleteProps<TOption, true, false, false>,
        "onChange" | "value" | "renderInput" | "isOptionEqualToValue"
    > & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik,
        label: string
        helperText?: string
        isOptionEqualToValue: (option: TOption, value: TValue) => boolean
        areOptionsEqual: (x: TOption, y: TOption) => boolean
        getOptionValue: (option: TOption) => TValue
    }
>

export function FormikMultipleAutoComplete<Values extends FormikValues, TOption, TValue>(
    props: FormikMultipleAutoCompleteProps<Values, TOption, TValue>,
) {
    const {
        name,
        label,
        helperText,
        multiple,
        options,
        isOptionEqualToValue,
        areOptionsEqual,
        getOptionValue,
        ...rest
    } = props

    const [field, meta, helpers] = useField<TValue[]>({ name, multiple: true })

    const optionValues = useMemo(() => {
        const result: TOption[] = field.value
            ? field.value.flatMap((value) => {
                  const foundOption = options.find((option) => isOptionEqualToValue(option, value))
                  if (foundOption === undefined) return []
                  return [foundOption]
              })
            : []
        return result
    }, [field.value, options])

    const handleChange = useCallback(
        (_event: SyntheticEvent<Element, Event>, options: AutocompleteValue<TOption, true, false, false>) => {
            const values = options.map((option) => getOptionValue(option))
            helpers.setValue(values)
        },
        [field, name],
    )

    return (
        <Autocomplete
            multiple
            options={options}
            value={optionValues}
            isOptionEqualToValue={areOptionsEqual}
            onBlur={field.onBlur}
            onChange={handleChange}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={label}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={(meta.touched && meta.error) || helperText}
                />
            )}
        />
    )
}

type FormikDropdownProps<Values extends FormikValues> = Readonly<
    StrictOmit<SelectProps, "error" | "value" | "name" | "children"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
        children: ReactNode // make children required
        fullWidth?: boolean
    }
>

export function FormikDropdown<Values extends FormikValues>({
    name,
    label,
    fullWidth,
    children,
    onBlur,
    onChange,
    ...rest
}: FormikDropdownProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)

    const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            field.onBlur(e)
            onBlur?.(e)
        },
        [onBlur],
    )

    const handleChange = useCallback(
        (e: SelectChangeEvent<unknown>, child: ReactNode) => {
            field.onChange(e)
            onChange?.(e, child)
        },
        [onChange],
    )

    return (
        <FormControl fullWidth={fullWidth} error={meta.touched && Boolean(meta.error)}>
            <InputLabel id={name + "_label"} size={rest.size === "small" ? "small" : "normal"}>
                {label}
            </InputLabel>
            <Select
                labelId={name + "_label"}
                id={name + "_label"}
                name={name}
                onBlur={handleBlur}
                onChange={handleChange}
                value={field.value}
                label={label}
                {...rest}
            >
                {children}
            </Select>
            {meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikPasswordField<Values extends FormikValues>(props: FormikTextFieldProps<Values>) {
    const [showPassword, setShowPassword] = useState(false)

    const toggleShowPassword = useCallback(() => setShowPassword((prev) => !prev), [setShowPassword])

    return (
        <FormikTextField
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    )
}

export function FormikPinField<Values extends FormikValues>(props: FormikTextFieldProps<Values>) {
    const [showPin, setShowPin] = useState(false)

    const toggleShowPin = useCallback(() => setShowPin((prev) => !prev), [setShowPin])

    return (
        <FormikTextField
            type={showPin ? "text" : "password"}
            autoComplete="off"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={toggleShowPin}>{showPin ? <Visibility /> : <VisibilityOff />}</IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    )
}

export type FormikCheckboxProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<
        StrictOmit<CheckboxProps, "name" | "value" | "checked" | "onChange"> & {
            name: StringKeyOf<Values>
            value?: string | number | readonly string[] // MUI types value prop as unknown but Formik only supports these types
            helperText?: string
            onLabelClick?: (e: MouseEvent<HTMLLabelElement>) => void
        }
    >
>

export function FormikCheckbox<Values extends FormikValues>({
    name,
    value,
    helperText,
    children,
    onLabelClick,
    ...rest
}: FormikCheckboxProps<Values>) {
    const [field, meta] = useField({ type: "checkbox", name, value })

    return (
        <FormControl error={Boolean(meta.error)}>
            <FormControlLabel
                label={children}
                control={
                    <Checkbox name={name} value={value} checked={field.checked} onChange={field.onChange} {...rest} />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error || helperText}</FormHelperText>
        </FormControl>
    )
}

type FormikRadioListItemProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<{
        name: StringKeyOf<Values>
        value: string | number | undefined
    }>
>

export function FormikRadioListItem<Values extends FormikValues>({
    name,
    value,
    children,
}: FormikRadioListItemProps<Values>) {
    const [field, _, helpers] = useField({ type: "radio", name, value })

    return (
        <>
            <ListItem button onClick={() => helpers.setValue(value)}>
                <ListItemText primary={children} />
                <ListItemSecondaryAction>
                    <Radio name={name} value={value} checked={field.checked} onChange={() => helpers.setValue(value)} />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
        </>
    )
}

type FormikSubmitButtonProps = StrictOmit<StandardLoadingButtonProps, "disabled" | "loading"> & {
    parentDisabled?: boolean
}

export const FormikSubmitButton = forwardRef(
    ({ children, parentDisabled, ...rest }: FormikSubmitButtonProps, ref: Ref<HTMLButtonElement>) => {
        const { isValid, isSubmitting, submitCount } = useFormikContext()

        const disabled = parentDisabled || (!isValid && submitCount > 0)

        return (
            <StandardLoadingButton ref={ref} type="submit" disabled={disabled} loading={isSubmitting} {...rest}>
                {children}
            </StandardLoadingButton>
        )
    },
)

type FormikSwitchProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<
        StrictOmit<SwitchProps, "name" | "value" | "checked" | "onChange"> & {
            name: StringKeyOf<Values>
            value?: string | number | readonly string[] // MUI types value prop as unknown but Formik only supports these types
            onLabelClick?: (e: MouseEvent<HTMLLabelElement>) => void
            formSidebarHeading?: boolean
            labelPlacement?: "end" | "start" | "top" | "bottom"
        }
    >
>

export function FormikSwitch<Values extends FormikValues>({
    name,
    value,
    children,
    onLabelClick,
    formSidebarHeading,
    labelPlacement = "start",
    ...rest
}: FormikSwitchProps<Values>) {
    const [field, meta] = useField({ type: "checkbox", name, value })
    const { classes, cx } = useStyles()
    return (
        <FormControl
            className={cx(classes.switch, {
                [classes.formSidebarHeadingPadding]: formSidebarHeading,
            })}
            error={Boolean(meta.error)}
        >
            <FormControlLabel
                className={classes.switchLabel}
                labelPlacement={labelPlacement}
                label={
                    formSidebarHeading ? (
                        <Typography className={classes.formSidebarHeading} variant="h3">
                            {children}
                        </Typography>
                    ) : (
                        children
                    )
                }
                control={
                    <Switch name={name} value={value} checked={field.checked} onChange={field.onChange} {...rest} />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error}</FormHelperText>
        </FormControl>
    )
}

export function FormikChip<Values extends FormikValues>({
    name,
    value,
    children,
    onLabelClick,
    ...rest
}: FormikCheckboxProps<Values>) {
    const [field, meta, helpers] = useField({ type: "checkbox", name, value })
    const { classes, cx } = useStyles()

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <FormControlLabel
                className={cx(classes.customChip, {
                    [classes.customChipChecked]: field.checked,
                })}
                label={children}
                labelPlacement="start"
                control={
                    <Checkbox
                        className={classes.hiddenField}
                        name={name}
                        value={value}
                        onChange={field.onChange}
                        {...rest}
                    />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error}</FormHelperText>
        </FormControl>
    )
}

type FormikTimePickerProps<Values extends FormikValues, TDate> = Readonly<
    StrictOmit<TimePickerProps<TDate>, "name" | "value" | "onChange"> & {
        name: StringKeyOf<Values>
        value?: string
        forceOpen?: boolean
        showMobilePicker?: boolean
    }
>

export function FormikTimePicker<Values extends FormikValues, TDate>({
    name,
    value,
    label,
    disabled = false,
    forceOpen = false,
    showMobilePicker = true, // clock
    ...rest
}: FormikTimePickerProps<Values, TDate>) {
    const [field, meta, helpers] = useField<TDate>({ type: "text", name, value })
    const { classes } = useStyles()
    const [open, setOpen] = useState(false)
    const step = 5

    const [localValue, setLocalValue] = useState<TDate | null>(field.value ?? null)

    useEffect(() => {
        if (forceOpen && !open) {
            setOpen(true)
        }
    }, [forceOpen])

    useEffect(() => {
        if (field.value !== localValue) {
            setLocalValue(field.value ?? null)
        }
    }, [field.value])

    const roundTime = (val: TDate): TDate => {
        if (val && typeof (val as any).toFormat === "function" && DateTime.isDateTime(val as any)) {
            const dt = val as unknown as DateTime
            return dt.set({
                minute: Math.round(dt.minute / step) * step,
                second: 0,
                millisecond: 0,
            }) as any as TDate
        }
        return val
    }

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <TimePicker<TDate>
                label={label}
                value={localValue}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                disabled={disabled}
                onChange={(val) => {
                    setLocalValue(val)
                }}
                onAccept={(val) => {
                    if (val) {
                        const rounded = roundTime(val)
                        helpers.setValue(rounded)
                    }
                }}
                minutesStep={step}
                viewRenderers={
                    showMobilePicker ? { hours: renderTimeViewClock, minutes: renderTimeViewClock } : undefined
                }
                slotProps={{
                    textField: {
                        onBlur: () => {
                            if (localValue) {
                                const rounded = roundTime(localValue)
                                helpers.setValue(rounded)
                            }
                        },
                        variant: "outlined",
                        size: "small",
                        fullWidth: true,
                    },
                    openPickerButton: { className: classes.timePickerIconButton },
                    popper: {
                        sx: {
                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                width: "1px",
                            },
                        },
                    },
                }}
                {...rest}
            />
            {meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikNoteInput<Values extends FormikValues>({
    name,
    helperText,
    ...rest
}: FormikTextFieldProps<Values>) {
    const { classes } = useStyles()
    const [field, meta, helpers] = useField(name)
    const [isEditable, setEditable] = useState(false)

    const handleEditClick = () => setEditable(true)
    const handleSaveClick = () => setEditable(false)
    const handleCancelClick = () => {
        setEditable(false)
        helpers.setValue("")
    }

    const endAdornment = isEditable ? (
        <InputAdornment position="end" className={isEditable ? classes.multilineAdornment : ""}>
            <IconButton size="small" onClick={handleSaveClick}>
                <FontAwesomeIcon icon={faCircleCheck} className={classes.icon} />
            </IconButton>
            <IconButton size="small" onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faCircleXmark} className={classes.icon} />
            </IconButton>
        </InputAdornment>
    ) : (
        <InputAdornment position="end">
            <IconButton size="small" className={classes.icon} onClick={handleEditClick}>
                <FontAwesomeIcon icon={faPen} />
            </IconButton>
        </InputAdornment>
    )

    const formikFieldProps = {
        ...field,
        disabled: !isEditable,
        error: meta.touched && Boolean(meta.error),
        helperText: (meta.touched && meta.error) || helperText,
        onBlur: field.onBlur,
        onChange: field.onChange,
    }

    return (
        <TextField
            className={classes.noteTextInput}
            multiline={isEditable}
            rows={isEditable ? 3 : 1}
            InputProps={{
                endAdornment,
            }}
            {...formikFieldProps}
            {...rest}
        />
    )
}

type FormikDatePickerProps<Values extends Record<string, any>, TDate> = {
    name: keyof Values
    label?: string
    disabled?: boolean
} & Omit<DatePickerProps<TDate>, "value" | "onChange" | "renderInput">

export function FormikDatePicker<Values extends Record<string, any>, TDate>({
    name,
    label,
    disabled = false,
    ...rest
}: FormikDatePickerProps<Values, TDate>) {
    const [field, meta, helpers] = useField<TDate | null>(name as string)
    const { setValue } = helpers
    const { submitCount } = useFormikContext<Values>()
    const { classes } = useStyles()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const handleChange = (newValue: TDate | null) => {
        setValue(newValue)
    }

    return (
        <FormControl className={classes.switch} error={showError}>
            <DatePicker<TDate>
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                {...rest}
            />
            {showError && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikDatePickerControlled<Values extends Record<string, any>, TDate>(
    props: FormikDatePickerProps<Values, TDate>,
) {
    const { name, label, disabled = false, ...rest } = props
    const [field, meta, helpers] = useField<TDate | null>(name as string)
    const { setValue } = helpers
    const { classes } = useStyles()
    const [open, setOpen] = useState(false)

    const handleChange = (newValue: TDate | null) => {
        setValue(newValue)
        setOpen(false)
    }

    useDebouncedEffect(() => {
        if (!field.value) {
            setOpen(true)
        }
    }, [field.value])

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <DatePicker<TDate>
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                {...rest}
            />
            {meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

type FormikDateTimePickerProps<Values extends Record<string, any>, TDate> = {
    name: keyof Values
    label?: string
    disabled?: boolean
} & Omit<DateTimePickerProps<TDate>, "value" | "onChange" | "renderInput">

export function FormikDateTimePicker<Values extends Record<string, any>, TDate>({
    name,
    label,
    disabled = false,
    ...rest
}: FormikDateTimePickerProps<Values, TDate>) {
    const [field, meta, helpers] = useField<TDate | null>(name as string)
    const { setValue } = helpers
    const { classes } = useStyles()

    const handleChange = (newValue: TDate | null) => {
        setValue(newValue)
    }

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <DateTimePicker<TDate>
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                {...rest}
            />
            {meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}
